// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePageSize from "@/hooks/usePageSize";
import useBulk from "@/hooks/useBulk";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useSelection from "@/hooks/useSelection";
import useTranslation from "@/hooks/useTranslation";
import type { TyLangValue, TyLead } from "@/types";
import { leadTitle, readLangValue } from "@/utils";
import LeadCard from "@/UI/cards/LeadCard";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import type { TyBulkField } from "@/UI/components/BulkBar/types";
import CrmStats from "@/UI/components/CrmStats";
import EmptyState from "@/UI/components/EmptyState";
import Paginator from "@/UI/components/Paginator";
import ScopeTabs from "@/UI/components/ScopeTabs";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import MultiSelectInput from "@/UI/inputs/MultiSelectInput";
import SelectInput from "@/UI/inputs/SelectInput";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import CloseDealPopup from "@/UI/popups/CloseDealPopup";
import CloseLeadPopup from "@/UI/popups/CloseLeadPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import InteractionFormPopup from "@/UI/popups/InteractionFormPopup";
import LeadFormPopup from "@/UI/popups/LeadFormPopup";
import LeadStagePopup from "@/UI/popups/LeadStagePopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The pipeline — the screen a sales agent opens first.
 *
 * **A list, not a board.** A kanban was the obvious shape and the wrong one here: this app is opened
 * on a phone during site visits, which is the one layout that does not survive that, and a member's
 * stages move forward only — a card dragged backwards would snap back with no explanation. Moving a
 * stage is a control that can say *why* it refused.
 *
 * Filters live in the URL, like the projects list and the drive, so "my open leads in Telal" is a
 * link a manager can send. They **replace** rather than push: narrowing a list is an action on what
 * is on screen, not a place.
 *
 * Closed leads are out of the list unless asked for. Closing exists to clear duplicates off the
 * board, so a board that still shows them has not done its job.
 *
 * **Mine or everyone's** is the only filter outside the panel. It is the one a sales agent sets on
 * arrival every day, and the row of clickable stage counts that used to sit here was neither: it
 * reported six numbers nobody opens a pipeline to read and doubled as a filter the panel already
 * had, so the same narrowing could be set two ways and disagree with itself.
 */
export default function CrmScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const { isOwner, role } = usePermission();
    const addComp = usePopup((state) => state.addComp);
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    const search = params.get("search") ?? "";
    const byName = params.get("name") ?? "";
    const byPhone = params.get("phone") ?? "";
    const stages = (params.get("stage") ?? "").split(",").filter(Boolean);
    const sources = (params.get("source") ?? "").split(",").filter(Boolean);
    const holder = params.get("assignee") ?? "";
    const closed = params.get("closed") ?? "0";
    const page = Math.max(1, Number(params.get("page")) || 1);

    const [isFiltering, setIsFiltering] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search);
    const [draftName, setDraftName] = useState(byName);
    const [draftPhone, setDraftPhone] = useState(byPhone);
    const [draftStages, setDraftStages] = useState<string[]>(stages);
    const [draftSources, setDraftSources] = useState<string[]>(sources);
    const [draftHolder, setDraftHolder] = useState(holder);
    const [draftClosed, setDraftClosed] = useState(closed);

    const isFiltered = !!search || !!byName || !!byPhone || stages.length > 0 || sources.length > 0 || !!holder || closed !== "0";
    const canDelete = isOwner || role === "manager";

    const leads = useFetch<{ leads: TyLead[]; counts: Record<string, number>; total: number }>("/crm/leads", {}, false);
    const action = useFetch("/crm/leads", {}, false);
    const selection = useSelection();

    const load = () => {
        const query: Record<string, unknown> = { limit: pageSize, page, closed };
        if (search.trim()) query.search = search.trim();
        if (byName.trim()) query.name = byName.trim();
        if (byPhone.trim()) query.phone = byPhone.trim();
        if (stages.length) query.stage = stages.join(",");
        if (sources.length) query.source = sources.join(",");
        if (holder === "mine") query.mine = 1;
        else if (holder) query.assignee = holder;
        leads.invoke({ query });
    };

    const bulk = useBulk({ path: "/crm/leads", selection, onDone: load });

    useEffect(() => {
        load();
        // A narrowed or paged list is a different list, so the picks made on the old one go with it.
        selection.clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, byName, byPhone, params.get("stage"), params.get("source"), holder, closed, page, pageSize]);

    useEffect(() => {
        if (!action.succeededAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        // Narrowing a list puts you back at its start: page 7 of a two-page result is a blank screen.
        if (!("page" in next)) merged.delete("page");
        setParams(merged, { replace: true });
    };

    const openForm = (lead?: TyLead) =>
        addComp({ name: "lead-form", Component: LeadFormPopup, place: "end", anim: "endSlide", details: { lead: lead ?? null, onDone: load } });

    const openActions = (lead: TyLead, anchor: DOMRect) => {
        const title = leadTitle(lead, t, (value) => readLangValue(value as TyLangValue[], lang));
        // No "open" entry: the whole card already does that, and a menu item for it was a second
        // way to do the obvious thing taking up the first row of every menu.
        const actions: TyAction[] = [
            { key: "edit", label: t.common.edit, icon: "edit", onSelect: () => openForm(lead) },
            /* How a selection starts. Adding to it afterwards is a press on the card itself. */
            { key: "select", label: t.bulk.select, icon: "check", onSelect: () => selection.toggle(lead._id) },
        ];

        // Moving a stage is not here any more: the card carries the trail, and it moves on a tap.
        // A loss still needs its reason, which is the one move the trail hands to this popup.
        if (!lead.closed && !(constants.leadTerminalStages as readonly string[]).includes(lead.stage)) {
            actions.push({
                key: "lost",
                label: t.crm.stages.lost,
                icon: "clear",
                onSelect: () =>
                    addComp({ name: "lead-stage", Component: LeadStagePopup, place: "end", anim: "endSlide", details: { lead, onDone: load } }),
            });
        }

        // Closing is for an untouched duplicate; reopening is an owner's or a manager's call.
        if ((lead.stage === "new" && !lead.closed) || (lead.closed && canDelete)) {
            actions.push({
                key: "closure",
                label: lead.closed ? t.crm.reopenLead : t.crm.closeLead,
                icon: "clear",
                onSelect: () =>
                    addComp({ name: "close-lead", Component: CloseLeadPopup, place: "end", anim: "endSlide", details: { lead, onDone: load } }),
            });
        }

        if (canDelete) {
            actions.push({
                key: "remove",
                label: t.common.remove,
                icon: "trash",
                danger: true,
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: t.common.remove,
                            message: t.crm.confirmRemoveLead.replace("{name}", title),
                            confirmLabel: t.common.remove,
                            onConfirm: () => action.invoke({ method: "DELETE", path: `/crm/leads/${lead._id}` }),
                        },
                    }),
            });
        }

        addComp({
            name: "lead-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    /**
     * **The stage is here, and it obeys the same rules the card's trail obeys** — the backend reads
     * them from one place for both doors, which is the only reason a second one is allowed to exist.
     * So `won` is not offered at all, and `lost` carries its reason.
     *
     * `assignees` is absent on purpose: handing eight deals to one agent is a *merge* question — add
     * somebody, or replace everyone? — and a dropdown has one answer per field.
     */
    const bulkFields: TyBulkField[] = [
        {
            key: "stage",
            label: t.crm.fields.stage,
            kind: "select",
            options: [...constants.leadWorkingStages, "lost" as const].map((item) => ({ value: item, label: t.crm.stages[item] })),
        },
        // Only once the loss is the answer: a reason without a move is a reason for nothing.
        {
            key: "lostReason",
            label: t.crm.fields.lostReason,
            kind: "select",
            when: (draft) => draft.stage === "lost",
            options: constants.leadLostReasons.map((item) => ({ value: item, label: t.crm.lostReasons[item] })),
        },
        {
            key: "source",
            label: t.crm.fields.source,
            kind: "select",
            options: [
                { value: "", label: t.properties.notSet },
                ...constants.leadSources.map((item) => ({ value: item, label: t.crm.sources[item] })),
            ],
        },
        {
            key: "financing",
            label: t.crm.fields.financing,
            kind: "select",
            options: [
                { value: "", label: t.properties.notSet },
                ...constants.leadFinancing.map((item) => ({ value: item, label: t.crm.financing[item] })),
            ],
        },
    ];

    return (
        <section className="crm-screen">
            <CrmStats reloadAt={action.succeededAt} />

            <ActionsBar
                tabs={[
                    { key: "customers", label: t.crm.customers },
                    { key: "leads", label: t.crm.leads },
                    { key: "installments", label: t.sales.installments },
                ]}
                activeTab="leads"
                onTab={(key) => key !== "leads" && navigate(key === "customers" ? "/crm/customers" : "/crm/installments")}
                addLabel={t.common.add}
                onAdd={() => openForm()}
                isFilterOpen={isFiltering}
                onFilterOpen={() => {
                    setDraftSearch(search);
                    setDraftName(byName);
                    setDraftPhone(byPhone);
                    setDraftStages(stages);
                    setDraftSources(sources);
                    setDraftHolder(holder);
                    setDraftClosed(closed);
                    setIsFiltering(true);
                }}
                hasFilters={isFiltered}
                onFilterApply={() => {
                    setIsFiltering(false);
                    apply({
                        search: draftSearch.trim() || null,
                        name: draftName.trim() || null,
                        phone: draftPhone.trim() || null,
                        stage: draftStages.join(",") || null,
                        source: draftSources.join(",") || null,
                        assignee: draftHolder || null,
                        closed: draftClosed === "0" ? null : draftClosed,
                    });
                }}
                onFilterClear={() => {
                    setDraftSearch("");
                    setDraftName("");
                    setDraftPhone("");
                    setDraftStages([]);
                    setDraftSources([]);
                    setDraftHolder("");
                    setDraftClosed("0");
                    setIsFiltering(false);
                    apply({ search: null, name: null, phone: null, stage: null, source: null, assignee: null, closed: null });
                }}
                onFilterCancel={() => setIsFiltering(false)}
                filterContent={
                    <>
                        {/* One box per field rather than one box for all of them: "Ahmed on an 05
                            number" is a question a single combined search cannot ask. */}
                        <FilterSearchInput value={draftSearch} onChange={setDraftSearch} label={t.common.search} placeholder={t.crm.searchLeads} />
                        <FilterSearchInput value={draftName} onChange={setDraftName} label={t.crm.fields.name} />
                        <FilterSearchInput value={draftPhone} onChange={setDraftPhone} label={t.crm.fields.phone} />
                        <MultiSelectInput
                            compact
                            value={draftStages}
                            onChange={setDraftStages}
                            options={constants.leadStages.map((item) => ({ value: item, label: t.crm.stages[item] }))}
                            label={t.crm.fields.stage}
                        />
                        <MultiSelectInput
                            compact
                            value={draftSources}
                            onChange={setDraftSources}
                            options={constants.leadSources.map((item) => ({ value: item, label: t.crm.sources[item] }))}
                            label={t.crm.fields.source}
                        />
                        <SelectInput
                            compact
                            label={t.crm.fields.assignees}
                            value={draftHolder}
                            onChange={setDraftHolder}
                            options={[
                                { value: "", label: t.crm.due.all },
                                { value: "mine", label: t.crm.assignedToMe },
                                { value: "none", label: t.crm.unassigned },
                            ]}
                        />
                        <SelectInput
                            compact
                            label={t.crm.fields.status}
                            value={draftClosed}
                            onChange={setDraftClosed}
                            options={[
                                { value: "0", label: t.crm.leads },
                                { value: "1", label: t.crm.closedBadge },
                                { value: "all", label: t.crm.due.all },
                            ]}
                        />
                    </>
                }
            />

            {/* Named for what it narrows: these are enquiries, and the same control over contacts
                one screen along says "contacts" instead. */}
            <ScopeTabs
                value={holder === "mine" ? "mine" : "all"}
                onChange={(scope) => apply({ assignee: scope === "mine" ? "mine" : null })}
                allLabel={t.crm.allInterests}
                mineLabel={t.crm.mineInterests}
            />

            {action.error && <p className="crm-screen__state crm-screen__state--error">{action.error}</p>}
            {leads.loading && <p className="crm-screen__state">{t.common.loading}</p>}
            {leads.error && <p className="crm-screen__state crm-screen__state--error">{leads.error}</p>}
            {!leads.loading && !leads.error && !leads.data?.leads.length && (
                <EmptyState icon="crm" title={t.crm.emptyLeads} actionLabel={t.crm.addLead} onAction={() => openForm()} />
            )}

            <div className="crm-screen__grid">
                {leads.data?.leads.map((lead) => (
                    <LeadCard
                        key={lead._id}
                        lead={lead}
                        isSelected={selection.has(lead._id)}
                        isPicking={selection.isSelecting}
                        // While the bar is up the card picks rather than opens — the gesture every
                        // file manager already taught people.
                        onOpen={() => (selection.isSelecting ? selection.toggle(lead._id) : navigate(`/crm/leads/${lead._id}`))}
                        // A closed enquiry is frozen, so it is offered no way to log a call against.
                        onContact={
                            lead.closed
                                ? undefined
                                : () =>
                                      addComp({
                                          name: "interaction-form",
                                          Component: InteractionFormPopup,
                                          anim: "scale",
                                          details: { leadId: lead._id, onDone: load },
                                      })
                        }
                        onActions={(anchor) => openActions(lead, anchor)}
                        onStage={
                            lead.closed
                                ? undefined
                                : (stage) => {
                                      // A loss asks why and a win asks which — both are forms, not stages.
                                      if (stage === "lost") {
                                          addComp({
                                              name: "lead-stage",
                                              Component: LeadStagePopup,
                                              place: "end",
                                              anim: "endSlide",
                                              details: { lead, onDone: load },
                                          });
                                      } else if (stage === "won") {
                                          addComp({ name: "close-deal", Component: CloseDealPopup, anim: "scale", details: { lead, onDone: load } });
                                      } else {
                                          action.invoke({ method: "PATCH", path: `/crm/leads/${lead._id}/stage`, data: { stage } });
                                      }
                                  }
                        }
                    />
                ))}
            </div>

            {/* One or the other, never both — `hooks/useSelection` says why the pager goes away. */}
            {selection.isSelecting ? (
                <BulkBar
                    count={selection.count}
                    fields={bulkFields}
                    busy={bulk.busy}
                    note={bulk.note}
                    onClear={selection.clear}
                    onApply={(set) => bulk.apply(set)}
                    // Deleting an enquiry is an owner's or a manager's call, in bulk as alone: it is
                    // the escape hatch from the one-way pipeline, and the loss reasons are the only
                    // measurement the funnel has.
                    onDelete={
                        canDelete
                            ? () =>
                                  bulk.confirmed({
                                      title: t.common.remove,
                                      message: t.bulk.confirmRemove.replace("{count}", String(selection.count)),
                                      confirmLabel: t.common.remove,
                                      doneLabel: t.bulk.removed,
                                  })
                            : undefined
                    }
                />
            ) : (
                <Paginator
                    page={page}
                    total={leads.data?.total ?? 0}
                    count={leads.data?.leads.length ?? 0}
                    onPage={(next) => apply({ page: next === 1 ? null : String(next) })}
                />
            )}
        </section>
    );
}
